import { Link } from "react-router-dom";
import { useRef } from "react";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Seo from "../components/Seo";
import VerseRotator from "../components/VerseRotator";
import LogoText from "../components/LogoText";
import Brand from "../components/Brand";
import ThreeTierPricing from "../components/ThreeTierPricing";
import CharterBonusBanner from "../components/CharterBonusBanner";
import FactBox from "../components/FactBox";
import LeadCapture from "../components/LeadCapture";
import RollingHero from "../components/RollingHero";
import MemberDirectory from "../components/MemberDirectory";
import { IMG } from "../lib/api";
import { BookOpen, Heart, GraduationCap, Mail, Users, ShoppingBag, Award, Sparkles, Cross, Gift, CheckCircle2, ArrowDown, ArrowRight, Search, PlayCircle, Lock, Crown, Footprints, Music, Palette, Globe, TrendingUp, NotebookPen } from "lucide-react";

// (The old `features` array was removed along with the duplicate FEATURES
// section — the "What's Inside FAITHARAMA" grid below replaces it with
// clickable Link cards that route to each real page.)

export default function Landing() {
  const freeRef = useRef(null);
  const scrollToFree = () => freeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className="min-h-screen bg-ivory">
      <Seo
        title="Christian Wellness & Ministry, Made Simple"
        description="Faitharama is Christian wellness and ministry for everyday believers — the 30-Day Faith & Wellness Challenge, AI writing tools, and a 50% affiliate program. Built by Michael Boyd for the family."
        canonical="https://faitharama.com/"
        keywords="Christian wellness, faith and health, 30 day Christian challenge, Christian affiliate program, ministry tools, AI Writer AI"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Faitharama",
          "url": "https://faitharama.com",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://faitharama.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      <SiteNav />

      {/* ===== ★ ROLLING HERO — continuous marquee of Mike's lifestyle + product images ===== */}
      <RollingHero />

      {/* ===== ★ SIGN IN THE DESERT — top hero, brand promise + the search ===== */}
      <section data-testid="desert-hero" className="relative overflow-hidden">
        {/* Sandstone gradient + subtle dune texture */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#E0F2FE] pointer-events-none"/>
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{backgroundImage:"radial-gradient(ellipse at 20% 90%, #2E9F3A 0%, transparent 60%), radial-gradient(ellipse at 80% 95%, #F39A1F 0%, transparent 50%)"}}/>
        <div className="relative max-w-5xl mx-auto px-6 md:px-8 pt-10 pb-14 text-center">
          <div className="inline-flex items-center gap-2 bg-[#0F172A] text-[#F39A1F] px-3 py-1 rounded-full text-[10px] tracking-wider-cap font-bold uppercase mb-4">★ EVERYONE WELCOME · CLEAN · FAMILY-FRIENDLY ★</div>

          <h1 className="font-serif-display text-red-wine uppercase leading-[1.05] text-3xl md:text-5xl lg:text-6xl max-w-3xl mx-auto text-balance" data-testid="hero-headline">
            One Platform For <span className="text-gold-deep">Faith, Wellness &amp; Income.</span>
          </h1>
          <div className="divider-gold"></div>
          <p className="text-base md:text-lg text-foreground max-w-xl mx-auto text-balance">
            Open to everyone — believer or not. We proclaim Jesus Christ as Lord in <strong>our own</strong> books and courses, but every tool, every product, and every payout is available to anyone who wants to build something clean and family-friendly. <strong className="text-red-wine"><Brand suffix=".com"/></strong> — every tool you need to be heard, found, and earn.
          </p>

          {/* ★ WELCOME VIDEO — Mike's 2-min message, centered above the search */}
          <div className="mt-8 max-w-2xl mx-auto" data-testid="welcome-card">
            <div className="relative overflow-hidden rounded-2xl shadow-xl text-left"
                 style={{ background: "#FFFFFF", border: "4px solid #2E9F3A" }}>
              <div className="absolute top-4 left-4 z-10 bg-[#2E9F3A] text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider-cap shadow-lg inline-flex items-center gap-1.5">
                <span className="w-2 h-2 bg-[#F39A1F] rounded-full animate-pulse"/>
                A Word From Faitharama
              </div>
              <video
                data-testid="welcome-video-player"
                className="w-full aspect-video object-cover"
                style={{ background: "#EAF6FC" }}
                controls
                playsInline
                preload="metadata"
              >
                <source src="/videos/welcome.mp4?v=20260430a" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="p-5 text-center"
                   style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)" }}>
                <h2 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight"
                    style={{ color: "#0F172A" }}>Welcome, friend.</h2>
                <div className="h-px w-12 my-3 mx-auto" style={{ background: "#2E9F3A" }}/>
                <p className="text-sm max-w-md mx-auto" style={{ color: "#0F172A" }}>
                  Hit play and let me tell you why I built <Brand/> — then scroll down and find your friend.
                </p>
                <div className="mt-4 flex flex-wrap gap-3 justify-center">
                  <Link to="/signup?plan=free" className="btn-gold inline-flex !py-2 !px-4 !text-xs" data-testid="welcome-cta-free">★ Start My 7-Day Free Trial</Link>
                  <Link to="/founder" className="text-xs font-bold uppercase tracking-wider-cap inline-flex items-center gap-2 px-2 py-2 hover:opacity-80" data-testid="welcome-cta-story" style={{ color: "#C9202E" }}>Read My Story →</Link>
                </div>
              </div>
            </div>
          </div>

          {/* The search */}
          <form
            data-testid="desert-search-form"
            onSubmit={e => { e.preventDefault(); const q = e.target.elements.q.value.trim(); if (q) window.location.href = `/u/${q.replace(/^https?:\/\//,'').replace(/\/.*$/,'').replace(/[^a-z0-9-]/gi,'').toLowerCase()}`; }}
            className="mt-7 max-w-2xl mx-auto"
          >
            <div className="bg-white rounded-2xl border-2 border-[#F39A1F] shadow-2xl p-5">
              <label htmlFor="desert-search" className="block font-serif-display text-xl md:text-2xl text-red-wine uppercase">Find the one who brought you here</label>
              <p className="text-sm text-foreground mt-1">Just type his first name or website…</p>
              <div className="relative mt-3">
                <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-foreground/50"/>
                <input
                  id="desert-search"
                  name="q"
                  type="search"
                  data-testid="desert-search-input"
                  placeholder="e.g.  yourname   or   yourname.com"
                  className="w-full bg-white border-2 border-foreground/15 focus:border-[#F39A1F] rounded-xl px-12 py-4 text-base outline-none transition"
                />
                <button type="submit" data-testid="desert-search-submit" className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-5 py-2 rounded-lg text-[11px] tracking-wider-cap font-bold uppercase transition">Find</button>
              </div>
              <Link to="/directory" data-testid="desert-browse-all" className="mt-3 inline-flex items-center gap-1 text-sm tracking-wider-cap font-bold text-foreground/80 hover:text-red-wine uppercase">Or browse the full Directory →</Link>
            </div>
          </form>
        </div>
      </section>

      {/* ═════════════════════════════════════════════════════════════════
          INTRO STATEMENT — Mike's "WHY I BUILT THIS SITE" personal note
          Anchored on 1 John 2:6. Warm cream/gold — opens with Mike's voice
          before any product pitch. Sits between desert-hero and flagship-book.
          ═════════════════════════════════════════════════════════════════ */}
      <section className="border-b-2"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)",
                        borderColor: "#2E9F3A" }}
               data-testid="landing-intro-statement">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] tracking-[0.3em] uppercase font-bold mb-5"
               style={{ background: "#EAF6FC", color: "#FFFFFF" }}>
            ★ FROM MIKE · WHY I BUILT THIS SITE
          </div>

          <p className="font-serif-display text-xl md:text-3xl leading-snug md:leading-snug"
             style={{ color: "#0F172A", textWrap: "balance" }}>
            Everybody's at a different place in their walk with God. But one thing's for certain —
            <strong style={{ color: "#0F172A" }}> a life that is filled with the Spirit of God</strong> can't help but want to produce fruit.
            Can't help but want to turn away from sin and walk in alignment with the footsteps of Jesus.
          </p>

          {/* 1 John 2:6 callout */}
          <div className="mt-8 mx-auto rounded-xl p-5 md:p-6 max-w-2xl"
               style={{ background: "#FFFFFF",
                        border: "2px solid #2E9F3A",
                        boxShadow: "0 6px 24px rgba(201, 162, 39, 0.18)" }}>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2"
                 style={{ color: "#C9202E" }}>
              ★ 1 JOHN 2:6
            </div>
            <p className="font-serif-display italic text-lg md:text-2xl leading-snug"
               style={{ color: "#0F172A" }}>
              "Anyone who claims to abide in Him must walk as Jesus walked."
            </p>
          </div>

          <p className="mt-8 text-lg md:text-2xl leading-relaxed"
             style={{ color: "#0F172A", textWrap: "balance" }}>
            That's <strong style={{ color: "#C9202E" }}>why I built this site.</strong> To get everybody's attention.
            For us all to re-examine our lives — because with God's Spirit inside of us,
            <strong style={{ color: "#0F172A" }}> life is so much better. So much more rewarding.</strong>
          </p>

          <div className="mt-7 text-xs tracking-[0.22em] uppercase font-bold"
               style={{ color: "#0F172A" }}>
            — Mike Boyd · Founder, Faitharama.com
          </div>
        </div>
      </section>


      {/* ═════════════════════════════════════════════════════════════════
          COMMISSIONS BOX — Three-Tier Split
          Front-and-center on the homepage. Mike's directive: every tier earns,
          rate scales with what they pay each month.
          ═════════════════════════════════════════════════════════════════ */}
      <section className="border-b-2"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)",
                        borderColor: "#2E9F3A" }}
               data-testid="landing-commission-box">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] tracking-[0.3em] uppercase font-bold mb-4"
               style={{ background: "#EAF6FC", color: "#FFFFFF" }}>
            ★ EARN WITH FAITHARAMA
          </div>
          <h2 className="font-serif-display uppercase leading-[0.96] mx-auto"
              style={{ color: "#0F172A", textWrap: "balance",
                       fontSize: "clamp(2rem, 5vw, 3.6rem)" }}>
            Share What You Love.<br/>
            <span style={{ color: "#2E9F3A" }}>Keep A Piece Of Every Sale.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#0F172A" }}>
            <strong>You get 50% of what <Brand suffix=".com"/> gets.</strong> Example: they pay us $10, <strong>you get 50% of $10</strong>, <Brand suffix=".com"/> keeps the rest.
            <span className="block mt-2"><strong style={{ color: "#C9202E" }}>NO OTHER DEAL LIKE IT.</strong></span>
          </p>

          {/* THE BIG WHITE BOX — flat 50% for everyone, 3 plans show what tools you get */}
          <div className="mt-10 rounded-2xl overflow-hidden shadow-xl"
               style={{ background: "#FFFFFF", border: "4px solid #2E9F3A" }}>
            <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x"
                 style={{ borderColor: "#2E9F3A" }}>
              {[
                { name: "7-Day Free Trial", price: "$0",     blurb: "Try everything for 7 days. Earn 50% from day one.",             accent: "#0F172A" },
                { name: "Pro",  price: "$49/mo", blurb: "Stay in after your trial. Keep your 50%. More tools unlocked.", accent: "#2E9F3A" },
                { name: "Unlimited",      price: "$79/mo", blurb: "Top tier. Every tool. Same 50%. Forever.",                       accent: "#C9202E" },
              ].map((t) => (
                <div key={t.name}
                     data-testid={`commission-tier-${t.name.replace(/\s+/g,'-').toLowerCase()}`}
                     className="p-6 md:p-8 flex flex-col"
                     style={{ borderColor: "#2E9F3A" }}>
                  <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2"
                       style={{ color: t.accent }}>
                    ★ {t.name.toUpperCase()}
                  </div>
                  <div className="font-serif-display leading-none mb-1"
                       style={{ fontSize: "clamp(3.5rem, 8vw, 5.5rem)", color: t.accent, letterSpacing: "-0.04em" }}>50%</div>
                  <div className="text-sm tracking-[0.18em] uppercase font-bold mb-3"
                       style={{ color: "#0F172A" }}>
                    {t.price}
                  </div>
                  <p className="text-sm md:text-base flex-1" style={{ color: "#0F172A" }}>
                    {t.blurb}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup?plan=free" data-testid="commission-cta-free"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] shadow-lg hover:scale-[1.03] transition"
                  style={{ background: "#2E9F3A", color: "#FFFFFF" }}>
              Start 7-Day Free Trial · Earn 50%
            </Link>
            <Link to="/pricing" data-testid="commission-cta-upgrade"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] transition hover:scale-[1.03]"
                  style={{ background: "#FFFFFF", color: "#0F172A", border: "2px solid #0F172A" }}>
              See All Tiers →
            </Link>
          </div>

          {/* ★ DO THE MATH — recurring monthly earnings for every member they bring ★ */}
          <div className="mt-10 mx-auto max-w-4xl rounded-2xl shadow-xl overflow-hidden"
               data-testid="commission-math"
               style={{ background: "linear-gradient(135deg, #0F172A 0%, #1B2D4E 100%)", border: "3px solid #F39A1F" }}>
            <div className="px-6 py-5 text-center" style={{ background: "#F39A1F", color: "#0F172A" }}>
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold">★ DO THE MATH ★</div>
              <div className="font-serif-display uppercase leading-tight mt-1" style={{ fontSize: "clamp(1.25rem, 2.4vw, 1.75rem)" }}>
                Every Member You Bring Pays You Every Month
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 px-6 py-7 text-center text-white">
              <div>
                <div className="font-serif-display leading-none" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#F39A1F" }}>$24.50</div>
                <div className="text-xs tracking-[0.22em] uppercase font-bold mt-1.5">Per Month · Per Pro Member ($49)</div>
              </div>
              <div className="sm:border-x" style={{ borderColor: "#F39A1F55" }}>
                <div className="font-serif-display leading-none" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#F39A1F" }}>$39.50</div>
                <div className="text-xs tracking-[0.22em] uppercase font-bold mt-1.5">Per Month · Per Unlimited Member ($79)</div>
              </div>
              <div>
                <div className="font-serif-display leading-none" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#F39A1F" }}>$245</div>
                <div className="text-xs tracking-[0.22em] uppercase font-bold mt-1.5">Per Month · 10 Pro Members. Recurring.</div>
              </div>
            </div>
            <div className="px-6 pb-5 text-center text-white/90 text-sm md:text-base">
              <strong className="text-[#F39A1F]">50%. Every month. Forever.</strong> Bring one friend, get half their plan in your pocket — for as long as they stay.
            </div>
          </div>

          {/* ★ NEW FAITHARAMA SLOGAN — Mike's words, big and unmissable */}
          <div className="mt-10 mx-auto max-w-3xl rounded-xl px-6 py-5 shadow-lg"
               data-testid="faitharama-slogan"
               style={{ background: "#FFFFFF", border: "3px solid #F39A1F" }}>
            <p className="font-serif-display uppercase leading-tight tracking-tight"
               style={{ color: "#0F172A", fontSize: "clamp(1.25rem, 2.6vw, 1.85rem)", textWrap: "balance" }}>
              <Brand suffix=".com"/> <strong style={{ color: "#2E9F3A" }}>believes</strong> everyone has a <strong style={{ color: "#C9202E" }}>successful business</strong> inside of them!!
            </p>
          </div>

          <p className="mt-5 text-xs italic max-w-xl mx-auto" style={{ color: "#0F172A" }}>
            ★ 50% of what <Brand suffix=".com"/> collects — same on digital and physical products. Apply direct with any brand for their own separate rate.
          </p>
        </div>
      </section>


      {/* ═════════════════════════════════════════════════════════════════
          ★ FAST START PROGRAM / $1,000 in 90 days. Mike's flagship
          paid entry product. Image-led, one big BUY button, no clutter.
          ═════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden border-y-2"
               style={{ background: "#FFFFFF", borderColor: "#2E9F3A" }}
               data-testid="landing-fast-start">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
          <Link to="/starter-guide"
                data-testid="landing-fast-start-image"
                className="inline-block group">
            <img src="/free-guide/fast-start-bundle.png"
                 alt="$1,000 in 90 days — Faitharama Fast Start Guide for included"
                 className="block w-full max-w-3xl mx-auto rounded-xl shadow-2xl group-hover:scale-[1.02] transition"
                 style={{ border: "4px solid #2E9F3A" }}
                 loading="lazy" />
          </Link>
          <div className="mt-7">
            <Link to="/starter-guide"
                  data-testid="landing-fast-start-cta"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-md text-base md:text-lg font-bold uppercase tracking-[0.18em] text-white shadow-xl hover:scale-[1.03] transition"
                  style={{ background: "#EAF6FC", border: "2px solid #2E9F3A" }}>
              Get The Fast Start Guide — included <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-sm mt-3 italic" style={{ color: "#0F172A" }}>
              The same 90-day playbook Faitharama members use to make their first $1,000 online.
            </p>
          </div>
        </div>
      </section>

      <section ref={freeRef} id="free-membership-hero" data-testid="hero-section" className="relative overflow-hidden bg-ivory">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-10 md:pt-14 pb-12 md:pb-16">
          {/* Logo — plain, no plaque, no box, same as everywhere else */}
          <div className="flex justify-center mb-6">
            <LogoText size="xl" as="span" />
          </div>

          {/* GIANT HEADLINE — one line across, no FAITHARAMA word so logo stays untouched */}
          <h1 className="font-serif-display text-center uppercase leading-[1.05] text-red-wine" style={{fontSize:'clamp(2.5rem, 6vw, 5.5rem)'}}>
            Get Your <span className="text-gold-deep">FREE</span> Membership
          </h1>
          <div className="divider-gold"></div>

          {/* PRIMARY CTA — DEAD CENTER under the headline (Mike's request) */}
          <div className="flex flex-col items-center gap-3 mt-2">
            <Link to="/signup?plan=free" className="bg-[#F39A1F] text-[#0F172A] px-10 py-5 rounded-md font-bold uppercase tracking-wider-cap text-lg md:text-xl hover:bg-[#F39A1F]/90 transition shadow-2xl inline-flex items-center gap-3 hover:scale-[1.02]" data-testid="hero-cta-free-signup">
              <Gift className="w-6 h-6"/>★ Start 7-Day Free Trial ★
            </Link>
            <div className="text-sm tracking-wider-cap text-foreground font-bold">100% FREE TRIAL · NO CREDIT CARD · 30 SECONDS</div>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
              <Link data-testid="hero-cta-trial" to="/signup" className="bg-red-wine text-white px-5 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs hover:bg-red-wine/90 transition">Get All Access · $49/mo</Link>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start mt-12">
            {/* LEFT — what's in your 7-day free trial */}
            <div className="fade-up">
              <p className="text-base md:text-lg lg:text-xl text-foreground font-semibold lg:whitespace-nowrap">
                The all-in-one platform <span className="text-red-wine">— no credit card needed. Try it free for 7 days.</span>
              </p>
              <ul className="mt-6 space-y-2.5 text-base text-foreground font-medium">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/>1 free funnel · 50 contacts</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><strong className="text-red-wine">Flat 50% commission on every sale — same for trial members and paid members ($49 Builder / $79 Pro)</strong></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/>Browse 100 funnel templates · Christian Store · Book Store</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/>Cancel anytime · Keep your affiliate link forever</li>
              </ul>
            </div>

            {/* RIGHT — flipbook-only form (clearly NOT membership) */}
            <div className="lg:sticky lg:top-24">
              <div className="bg-white text-foreground rounded-2xl shadow-[0_15px_50px_-12px_rgba(139,0,0,0.25)] p-7 ring-1 ring-[#F39A1F]/40">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-1 bg-white text-red-wine border border-[#F39A1F] rounded-full px-3 py-1 mb-2">
                    <BookOpen className="w-3.5 h-3.5"/>
                    <span className="text-[10px] tracking-wider-cap font-bold uppercase">Free Guide · Different Thing</span>
                  </div>
                  <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase">Just Want The Guide?</h2>
                  <p className="text-sm text-foreground mt-2">Drop your email — guide opens instantly. <strong>No account created.</strong></p>
                  <p className="text-sm text-foreground/90 mt-2 italic">For the Free <Brand/> membership, click the gold button at the top ↑</p>
                </div>
                <LeadCapture source="hero-flipbook-only" />
              </div>
            </div>
          </div>

          <VerseRotator />
        </div>
      </section>

      {/* ===== SECONDARY HERO — Mission statement ===== */}
      <section className="bg-[#EAF6FC] border-y border-[#F39A1F]/30">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-12 text-center">
          <div className="text-[11px] tracking-wider-cap text-gold-deep mb-3 font-bold">An Exclusive Christian Platform</div>
          <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.05] uppercase">
            <span className="text-red-wine">Building God's Kingdom</span> <span className="text-gold-deep">through Faith &amp; Wellness</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground max-w-2xl mx-auto">
            For small business owners, coaches, authors, and wellness pros. <span className="text-red-wine font-bold">A safe place for Christians. Also actually fun.</span>
          </p>
        </div>
      </section>

      {/* COLORING PAGES PROMO — bright, surfaced on the homepage so Grandma
          finds it without having to dig through the nav. */}
      <section data-testid="home-coloring-promo" className="max-w-6xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <Link to="/coloring-pages" className="block rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 transition group"
              style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #D9EEFB 50%, #EAF6FC 100%)", border: "4px solid #F39A1F" }}>
          <div className="grid md:grid-cols-[1fr_280px] items-center gap-6 p-7 md:p-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.28em] uppercase font-bold mb-3 text-white"
                   style={{ background: "#2E9F3A" }}>
                ★ FREE FOR THE GRANDKIDS
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl uppercase leading-[1.05]"
                  style={{ color: "#0F172A", textWrap: "balance" }}>
                52 Bible-Story Coloring Pages.<br/>Print One At A Time.
              </h2>
              <div className="mt-5 inline-block rounded-xl px-5 py-3"
                   style={{ background: "#FFFFFF", border: "2px dashed #F39A1F" }}>
                <span className="font-serif-display text-lg md:text-xl italic" style={{ color: "#0F172A" }}>
                  &ldquo;The Automatic Babysitter — thank you, Faitharama.com.&rdquo;
                </span>
              </div>
              <p className="mt-5 text-base md:text-lg text-[#0F172A] max-w-xl leading-relaxed">
                Hand the kids a page. Hand them the crayons. <strong>Ten quiet minutes.</strong> No login. No charge.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.03] transition"
                   style={{ background: "#2E9F3A" }}>
                Open The Coloring Library
              </div>
            </div>
            <div className="hidden md:flex items-center justify-center">
              <div className="rounded-2xl bg-white p-3 shadow-md transform -rotate-3 group-hover:rotate-0 transition"
                   style={{ border: "3px solid #F39A1F" }}>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/coloring/01-god-creates-the-world.png`}
                  alt="Sample coloring page — God Creates The World"
                  className="block w-[240px] h-auto"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </Link>
      </section>

      {/* Cool-Christian strip + Member Directory — these were orphaned when we deleted the old book-promo section. Now wrapped in their own clean section. */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-4 text-center">
          <div className="p-4 rounded-xl bg-white border border-border/60 flex items-center justify-center gap-3" data-testid="vibe-respect">
            <Cross className="w-5 h-5 text-gold-deep flex-shrink-0" strokeWidth={1.75} />
            <span className="font-overline uppercase tracking-wider-cap text-sm text-foreground">Respect the Cross</span>
          </div>
          <div className="p-4 rounded-xl bg-white border border-border/60 flex items-center justify-center gap-3" data-testid="vibe-love">
            <Heart className="w-5 h-5 text-red-wine flex-shrink-0" strokeWidth={1.75} />
            <span className="font-overline uppercase tracking-wider-cap text-sm text-foreground">Love the People</span>
          </div>
          <div className="p-4 rounded-xl bg-white border border-border/60 flex items-center justify-center gap-3" data-testid="vibe-fun">
            <Sparkles className="w-5 h-5 text-gold-deep flex-shrink-0" strokeWidth={1.75} />
            <span className="font-overline uppercase tracking-wider-cap text-sm text-foreground">Have Fun Doing It</span>
          </div>
        </div>

        {/* MEMBER DIRECTORY — Mike's "search engine on the front page" */}
        <div className="mt-14">
          <MemberDirectory compact={true} max={6} />
        </div>
      </section>

      {/* The old FEATURES section (was: "One Platform. Whole Ministry." with
          6 non-clickable cards) was REMOVED — duplicated the newer "What's
          Inside FAITHARAMA" grid above, which has clickable Links. Mike's
          rule: "see if it's already said." It was. */}


      {/* GOLI GUMMIES + MERCH */}
      <section id="market" className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid md:grid-cols-2 gap-12 items-center">
        <div className="order-2 md:order-1">
          <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/4jq13qjg_goli.com%20gummies.png" className="rounded-2xl shadow-xl w-full object-contain bg-black aspect-[4/3]" alt="Goli Gummies — full lineup" />
        </div>
        <div className="order-1 md:order-2">
          <div className="text-xs tracking-wider-cap text-gold-deep mb-3">Your Body is a Temple</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-red-wine mb-5 uppercase leading-tight">Easy-To-Swallow Gummies</h2>
          <p className="text-lg text-foreground mb-3">Mike's pick for clean, plant-based wellness — apple cider vinegar, sleep, immune support, ashwagandha. <strong>No more horse-pill struggles.</strong></p>
          <p className="text-base text-foreground mb-3">
            No recruiting downlines · No monthly quotas · Anyone can sign up.
          </p>
          <p className="text-base text-foreground mb-6 italic">Earn <strong className="text-[#22693C]">20% lifetime commission</strong> on anyone you refer.</p>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/goli" className="btn-gold" data-testid="goli-see-all-cta">See Supplement Deals →</Link>
            <Link to="/pricing" className="text-sm text-red-wine font-bold uppercase tracking-wider-cap hover:text-gold-deep transition" data-testid="nutrition-cta">Unlock the Market</Link>
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW — full 3-tier card row so visitors see $0 / $49 / $79 */}
      <section id="pricing-preview" className="parchment-bg border-y border-border/60"
               data-testid="landing-pricing-preview">
        <ThreeTierPricing />
        <div className="text-center pb-10">
          <Link data-testid="pricing-cta" to="/pricing"
                className="btn-outline-gold inline-block">
            Compare All 3 Plans Side-By-Side →
          </Link>
        </div>
      </section>

      {/* ═══ INSIDE YOUR BACK OFFICE — what visitors get + what's locked ═══ */}
      <BackOfficePreview />


      {/* ═══ AFFILIATE MARKETING 101 — BUSINESS LANE PROMO (light) ═══ */}
      <section className="border-b-2"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)", borderColor: "#2E9F3A" }}
               data-testid="landing-affiliate101-promo">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-16 grid md:grid-cols-[200px_1fr_auto] gap-6 md:gap-8 items-center">
          <div className="flex justify-center md:justify-start">
            <img src="/courses/pro-affiliate-101.png" alt="Affiliate Marketing 101 cover"
                 className="rounded-xl shadow-2xl"
                 style={{ width: 180, border: "4px solid #2E9F3A" }}/>
          </div>
          <div className="text-center md:text-left">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: "#C9202E" }}>
              ★ NEW · COURSE 01 · FREE TO READ
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.02] mt-1"
                style={{ color: "#0F172A", textWrap: "balance" }}>
              The Super Affiliate Training Ground.
            </h2>
            <p className="mt-3 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "#0F172A" }}>
              We train you to earn. Plain-English attraction-marketing playbook — <strong>10 chapters, 90-day plan, no fluff.</strong> Open to everyone — believer or not. Plug into Faitharama&apos;s vetted partner products (UpNourish, Goli, GoGreen Hemp, Bee&amp;You, WellFit Treadmills) and earn a flat <strong style={{ color: "#2E9F3A" }}>50/50 split</strong> on every sale — same rate for everyone.
            </p>
          </div>
          <div className="text-center">
            <Link to="/business/affiliate-marketing-101"
                  data-testid="landing-affiliate101-cta"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition whitespace-nowrap"
                  style={{ background: "#2E9F3A" }}>
              <BookOpen className="w-5 h-5"/> Start Super Affiliate Training
            </Link>
          </div>
        </div>
      </section>

      {/* GLOBAL MISSION */}
      <section className="relative overflow-hidden border-y border-border/60">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.earthFromSpace} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/75 to-background/90" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-20 text-center">
          <div className="text-xs tracking-wider-cap text-gold-deep mb-3">Every Nation. Every Home.</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-red-wine uppercase">"Go Into All the World."</h2>
          <div className="divider-gold"></div>
          <p className="text-lg text-foreground max-w-2xl mx-auto italic">Mark 16:15 is our mandate. <Brand/> gives you the tools to reach them — from your kitchen table to the ends of the earth.</p>
        </div>
      </section>

      {/* WHAT'S INSIDE — replaces the old founder/church block as Mike requested
          (was wasted space). Now it's a clean LiveGood-style 6-card grid of
          plug-and-play courses and tools. Each card LINKS to the right page. */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-16">
        <div className="text-center mb-10">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: "#F39A1F" }}>★ EVERYTHING IN THE BUNDLE ★</div>
          <h2 className="font-serif-display text-4xl md:text-5xl uppercase leading-tight" style={{ color: "#1F2937" }}>
            Everything Your Ministry Needs.
          </h2>
          <div className="mx-auto mt-3 w-24 h-1 rounded-full" style={{ background: "#F39A1F" }}/>
          <p className="mt-3 text-foreground max-w-2xl mx-auto">One login. One subscription. Every tool a small business needs to share, sell, and serve.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { title: "Fast Start Program",   to: "/market#streams",    Icon: GraduationCap, blurb: "$1,000/month in 90 days during your 7-day free trial · paperback + 30-day challenge + 5 videos · included.", color: "#F39A1F" },
            { title: "5 Income Streams",         to: "/market#streams",    Icon: Award,         blurb: "Multiple income streams — quit smoking, smoothies, fitness, hydration, exercise.", color: "#2E9F3A" },
            { title: "30-Day Academy",           to: "/dashboard/academy", Icon: BookOpen,      blurb: "30 interactive daily lessons — devotion, faith-building, action steps.", color: "#F39A1F" },
            { title: "Funnel Builder",           to: "/dashboard/funnels", Icon: Sparkles,      blurb: "One-click templates that capture leads on autopilot. Senior-friendly.", color: "#2E9F3A" },
            { title: "104-Email Engine",         to: "/dashboard/emails",  Icon: Mail,          blurb: "Two emails a week, set once — runs forever. Buyer & non-buyer tracks.", color: "#F39A1F" },
            { title: "Member Stores · Books",    to: "/books",             Icon: ShoppingBag,   blurb: "Sell books, supplements, merch — built-in affiliate tracking.", color: "#2E9F3A" },
          ].map(({ title, to, Icon, blurb, color }) => (
            <Link key={title} to={to}
                  data-testid={`landing-plug-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group rounded-2xl bg-white border-2 border-border hover:border-[#F39A1F] shadow-sm hover:shadow-2xl hover:-translate-y-0.5 transition p-5 flex flex-col">
              <div className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                   style={{ background: `${color}1A`, border: `2px solid ${color}` }}>
                <Icon className="w-5 h-5" style={{ color }}/>
              </div>
              <div className="font-serif-display text-xl text-[#1F2937]" style={{ fontWeight: 700 }}>{title}</div>
              <p className="text-sm text-foreground/85 mt-1 flex-1">{blurb}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase font-bold group-hover:gap-2 transition" style={{ color }}>
                Learn More →
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-9">
          <Link to="/founder" data-testid="landing-meet-mike" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] px-6 py-3 rounded-md border-2 transition" style={{ color: "#2E9F3A", borderColor: "#2E9F3A" }}>
            ★ Meet Mike (Founder)
          </Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.dramaticCross} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/85 to-background" />
        </div>
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-24 text-center">
          <Cross className="w-10 h-10 text-gold mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="font-serif-display text-4xl md:text-5xl text-red-wine uppercase">Your Ministry, Online &amp; Thriving.</h2>
          <p className="mt-4 text-lg text-foreground max-w-2xl mx-auto italic">"Go into all the world and preach the gospel." — Mark 16:15</p>
          <Link to="/signup" className="btn-primary inline-block mt-8" data-testid="final-cta">Begin Free Trial</Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   INSIDE YOUR BACK OFFICE — preview for VISITORS (before they sign up).
   Shows every feature, with locks on the paid-only ones. The point:
   let people see what they're missing.
   ════════════════════════════════════════════════════════════════════ */
function BackOfficePreview() {
  const ITEMS = [
    /* FREE for everyone */
    { tier: "free", group: "Free",    icon: "GraduationCap",title: "The Academy", desc: "6 plain-English courses on how to use every tool." },
    { tier: "free", group: "Free",    icon: "Heart",        title: "Prayer Box · Mike Reads Them",  desc: "Send Mike a prayer request, he prays over every one." },
    { tier: "free", group: "Free",    icon: "Footprints",   title: "Walk Tracker · Daily Step Goal",desc: "Faith + daily steps. Builds your streak." },
    { tier: "free", group: "Free",    icon: "Music",        title: "Mike's Jukebox",                desc: "Hand-picked Christian music. Free always." },
    { tier: "free", group: "Free",    icon: "Palette",      title: "Kids · Coloring Pages",         desc: "Print or save. For the whole family." },
    /* PAID — Pro */
    { tier: "paid", group: "Paid",    icon: "Sparkles",     title: "AI Writer — Full-Page Tool",    desc: "Sales copy, emails, blog posts in seconds." },
    { tier: "paid", group: "Paid",    icon: "Globe",        title: "Your Mirror Site",              desc: "Your own Faitharama page on a vanity URL." },
    { tier: "paid", group: "Paid",    icon: "ShoppingBag",  title: "Your Market · Sell Anything", desc: "List your own digital products, keep 100%." },
    { tier: "paid", group: "Paid",    icon: "Mail",         title: "Email Engine — 104 Rotations",  desc: "Pre-written email sequences, your name on them." },
    { tier: "paid", group: "Paid",    icon: "Award",        title: "50% Affiliate Commissions",     desc: "Flat 50% on every referral, forever." },
    { tier: "paid", group: "Paid",    icon: "Crown",        title: "Advanced Training", desc: "Mike's $1k/mo playbooks, locked vault." },
    { tier: "paid", group: "Paid",    icon: "TrendingUp",   title: "Market · Vetted Income Streams", desc: "Goli · Piurify · The Book · The Course · more coming." },
    { tier: "paid", group: "Paid",    icon: "NotebookPen",  title: "Pro Notes — Sync + Voice",      desc: "Daily Scripture, voice-to-text, zero ads." },
  ];
  const iconMap = { BookOpen, GraduationCap, Heart, Sparkles, ShoppingBag, Mail, Award, Users, Footprints, Music, Palette, Globe, Crown, TrendingUp, NotebookPen };
  return (
    <section id="back-office-preview" data-testid="back-office-preview"
             className="relative overflow-hidden border-y-2"
             style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 60%, #E0F2FE 100%)", borderColor: "#2E9F3A" }}>
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-15 pointer-events-none"
           style={{ background: "radial-gradient(circle, #2E9F3A 0%, transparent 70%)" }} />
      <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold mb-4"
               style={{ background: "#EAF6FC", color: "#E0F2FE" }}>
            <Sparkles className="w-3.5 h-3.5" /> ★ Inside Your Back Office
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl uppercase leading-[1.05]" style={{ color: "#0F172A", textWrap: "balance" }}>
            Here's <span style={{ color: "#2E9F3A" }}>everything</span> you get when you sign up.
          </h2>
          <p className="text-base md:text-lg mt-5 text-foreground italic">
            <strong className="not-italic" style={{ color: "#2E9F3A" }}>Five things are FREE forever.</strong> The locked ones unlock the day you upgrade to <strong className="not-italic" style={{ color: "#0F172A" }}>$49/mo Pro</strong>. No tiers, no hoops, no quotas.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-10" data-testid="back-office-preview-grid">
          {ITEMS.map((it, idx) => {
            const Icon = iconMap[it.icon] || Sparkles;
            const isPaid = it.tier === "paid";
            return (
              <div key={idx}
                   data-testid={`bo-feature-${idx}`}
                   className="relative rounded-xl bg-white border-2 p-4 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition flex flex-col"
                   style={{ borderColor: isPaid ? "rgba(91,31,31,0.35)" : "rgba(46,159,58,0.4)" }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                       style={{ background: isPaid ? "rgba(91,31,31,0.1)" : "rgba(46,159,58,0.12)" }}>
                    <Icon className="w-5 h-5" style={{ color: isPaid ? "#0F172A" : "#2E9F3A" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] tracking-[0.24em] uppercase font-bold mb-1"
                         style={{ color: isPaid ? "#0F172A" : "#2E9F3A" }}>
                      {isPaid ? "★ Active Member" : "★ 7-Day Free Trial"}
                    </div>
                    <div className="font-bold text-foreground text-sm leading-snug">{it.title}</div>
                    <p className="text-xs text-foreground/70 mt-1 leading-relaxed">{it.desc}</p>
                  </div>
                </div>
                {isPaid && (
                  <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] uppercase tracking-[0.22em] font-bold self-start"
                       style={{ background: "rgba(91,31,31,0.08)", color: "#0F172A", border: "1px solid rgba(91,31,31,0.25)" }}>
                    <Lock className="w-3 h-3" /> Locked Until $49/mo
                  </div>
                )}
                {!isPaid && (
                  <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] uppercase tracking-[0.22em] font-bold self-start"
                       style={{ background: "rgba(46,159,58,0.1)", color: "#2E9F3A", border: "1px solid rgba(46,159,58,0.3)" }}>
                    <CheckCircle2 className="w-3 h-3" /> Yours The Moment You Join
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link to="/signup?plan=free" data-testid="back-office-preview-cta-free"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:opacity-90 transition mr-3"
                style={{ background: "#2E9F3A" }}>
            <CheckCircle2 className="w-4 h-4" /> Start Free · See Inside
          </Link>
          <Link to="/signup?plan=faithful" data-testid="back-office-preview-cta-paid"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] shadow-lg hover:opacity-90 transition"
                style={{ background: "#2E9F3A", color: "#0F172A", border: "2px solid #0F172A" }}>
            <Crown className="w-4 h-4" /> Unlock Everything · $49/mo
          </Link>
        </div>
      </div>

      {/* "Listen Up" pulse animation */}
      <style>{`
        @keyframes listenUpPulse {
          0%, 100% { transform: scale(1);   box-shadow: 0 12px 40px rgba(201,32,46,0.35); }
          50%      { transform: scale(1.012); box-shadow: 0 20px 64px rgba(201,32,46,0.55); }
        }
        .listen-up-pulse { animation: listenUpPulse 3.4s ease-in-out infinite; }
      `}</style>
    </section>
  );
}
